/** @module @liquidefy/code-editor/filesystem/filesystem-ast @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  ASTNode,
  ASTQuery,
  ASTTransform,
  SymbolInfo,
  SymbolKind,
  LanguageId,
  Position,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — ASTVisitor, ASTSymbol, CallGraph, ImportGraph
// ═══════════════════════════════════════════════════════════════════════════════

/** Visitor hooks for AST traversal. */
export interface ASTVisitor {
  /** Called when entering a node. Return false to skip children. */
  readonly onEnter?: (node: ASTNode, depth: number) => boolean | void;
  /** Called when exiting a node. */
  readonly onExit?: (node: ASTNode, depth: number) => void;
}

/** A resolved symbol with full position information. */
export interface ASTSymbol {
  readonly name: string;
  readonly kind: SymbolKind;
  readonly range: Range;
  readonly selectionRange: Range;
  readonly uri: string;
  readonly containerName?: string;
}

/** Call graph node with caller/callee relationships. */
export interface CallGraph {
  /** Map from function name to the names it calls. */
  readonly calls: Map<string, string[]>;
  /** Map from function name to its callers. */
  readonly calledBy: Map<string, string[]>;
}

/** Import dependency map for a file. */
export interface ImportGraph {
  /** Map from import source to the names imported from it. */
  readonly imports: Map<string, string[]>;
  /** Map from name to the file it was exported from. */
  readonly exportedFrom: Map<string, string>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PARSING — parseFile
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Parses file content into an ASTNode tree.
 * This is a structural parser — it builds a synthetic AST from line/indent structure
 * when a language-specific parser is not wired in via IO. Production usage injects
 * tree-sitter or similar via the IO boundary.
 * @param content - File content to parse.
 * @param languageId - Language identifier for the content.
 * @returns EditorResult wrapping the root ASTNode.
 */
export function parseFile(
  content: string,
  languageId: LanguageId
): EditorResult<ASTNode> {
  if (typeof content !== 'string') {
    return err('INVALID_CONTENT', 'Content must be a string');
  }

  const lines = content.split('\n');
  const children = lines.map((lineText, index) => buildLineNode(lineText, index, languageId));

  const root: ASTNode = {
    type: 'document',
    range: {
      start: { line: 0, column: 0 },
      end: { line: lines.length - 1, column: lines[lines.length - 1]?.length ?? 0 },
    },
    children,
    language: languageId,
    name: 'root',
  };

  return ok(root, 'FILE_PARSED', `Parsed ${lines.length} lines as ${languageId}`);
}

/** Builds a line-level AST node. */
function buildLineNode(text: string, lineIndex: number, languageId: LanguageId): ASTNode {
  const indent = text.length - text.trimStart().length;
  return {
    type: classifyLine(text, languageId),
    range: {
      start: { line: lineIndex, column: 0 },
      end: { line: lineIndex, column: text.length },
    },
    children: [],
    language: languageId,
    name: text.trim().slice(0, 64) || undefined,
    value: indent,
  };
}

/** Classifies a line into a node type based on common patterns. */
function classifyLine(text: string, languageId: LanguageId): string {
  const t = text.trim();
  if (!t) return 'blank';
  if (t.startsWith('//') || t.startsWith('#') || t.startsWith('--')) return 'comment';
  if (/^(import|from|require|use|using|include)/.test(t)) return 'import';
  if (/^(export\s+)?(function|fn|def|fun|func|sub)\s+/.test(t)) return 'function_declaration';
  if (/^(export\s+)?(class|interface|struct|enum|trait|type)\s+/.test(t)) return 'type_declaration';
  if (/^(export\s+)?(const|let|var|val|mut|static)\s+/.test(t)) return 'variable_declaration';
  if (/^return\b/.test(t)) return 'return_statement';
  if (/^if\b/.test(t)) return 'if_statement';
  if (/^(for|while|loop)\b/.test(t)) return 'loop_statement';
  return 'expression';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 TRAVERSAL — traverseAST
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Performs a depth-first traversal of an AST, calling visitor hooks on each node.
 * @param root - Root node to start traversal from.
 * @param visitor - Visitor with optional onEnter/onExit callbacks.
 */
export function traverseAST(root: ASTNode, visitor: ASTVisitor): void {
  traverseNode(root, visitor, 0);
}

function traverseNode(node: ASTNode, visitor: ASTVisitor, depth: number): void {
  const shouldDescend = visitor.onEnter?.(node, depth);
  if (shouldDescend === false) {
    visitor.onExit?.(node, depth);
    return;
  }
  for (const child of node.children) {
    traverseNode(child, visitor, depth + 1);
  }
  visitor.onExit?.(node, depth);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 QUERY — queryAST
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Queries an AST for nodes matching a pattern.
 * Pattern is matched against node.type using substring matching (similar to CSS selectors).
 * @param root - Root AST node to search.
 * @param selector - Node type pattern to match (e.g. 'function_declaration').
 * @returns Array of matching ASTNode entries.
 */
export function queryAST(root: ASTNode, selector: string): ASTNode[] {
  const results: ASTNode[] = [];
  traverseAST(root, {
    onEnter(node) {
      if (node.type.includes(selector) || selector === '*') {
        results.push(node);
      }
    },
  });
  return results;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 SYMBOL EXTRACTION — extractSymbols
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extracts all named symbols (functions, classes, variables, etc.) from an AST.
 * @param root - Root AST node.
 * @param languageId - Language being analyzed.
 * @returns Array of ASTSymbol entries.
 */
export function extractSymbols(root: ASTNode, languageId: LanguageId): ASTSymbol[] {
  const SYMBOL_NODE_TYPES = new Set([
    'function_declaration',
    'type_declaration',
    'variable_declaration',
  ]);

  const KIND_MAP: Record<string, SymbolKind> = {
    function_declaration: 'function',
    type_declaration: 'class',
    variable_declaration: 'variable',
  };

  const symbols: ASTSymbol[] = [];

  traverseAST(root, {
    onEnter(node) {
      if (!SYMBOL_NODE_TYPES.has(node.type) || !node.name) return;
      symbols.push({
        name: node.name,
        kind: KIND_MAP[node.type] ?? 'variable',
        range: node.range,
        selectionRange: {
          start: node.range.start,
          end: { line: node.range.start.line, column: node.range.start.column + node.name.length },
        },
        uri: '',
      });
    },
  });

  return symbols;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 REFERENCES — findReferences
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Finds all AST nodes whose name matches the given symbol.
 * @param root - Root AST node.
 * @param symbol - Symbol to find usages of.
 * @returns Array of ASTNode where node.name matches symbol.name.
 */
export function findReferences(root: ASTNode, symbol: ASTSymbol): ASTNode[] {
  const results: ASTNode[] = [];
  traverseAST(root, {
    onEnter(node) {
      if (node.name === symbol.name) results.push(node);
    },
  });
  return results;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 TRANSFORM — transformAST
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Applies a pure transform to an AST, returning a new tree.
 * The transformer function receives each matching node and returns a replacement node.
 * @param root - Root AST node.
 * @param transformer - Transform description with query pattern and replacement.
 * @returns EditorResult wrapping the new (transformed) ASTNode tree.
 */
export function transformAST(
  root: ASTNode,
  transformer: ASTTransform
): EditorResult<ASTNode> {
  function transformNode(node: ASTNode): ASTNode {
    const matches = node.type.includes(transformer.query.pattern) ||
      transformer.query.pattern === '*';
    const transformedChildren = node.children.map(transformNode);

    if (!matches) {
      return { ...node, children: transformedChildren };
    }

    const replacement = typeof transformer.replacement === 'function'
      ? transformer.replacement(node)
      : transformer.replacement;

    return {
      ...node,
      children: transformedChildren,
      name: replacement || node.name,
    };
  }

  return ok(
    transformNode(root),
    'AST_TRANSFORMED',
    `AST transformed with pattern "${transformer.query.pattern}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 POSITION QUERIES — getNodeAtPosition, getSymbolAtPosition
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Finds the innermost AST node containing the given position.
 * @param root - Root AST node.
 * @param position - Cursor position to query.
 * @returns The innermost ASTNode at the position, or null if none found.
 */
export function getNodeAtPosition(root: ASTNode, position: Position): ASTNode | null {
  function nodeContains(node: ASTNode): boolean {
    const { start, end } = node.range;
    if (position.line < start.line || position.line > end.line) return false;
    if (position.line === start.line && position.column < start.column) return false;
    if (position.line === end.line && position.column > end.column) return false;
    return true;
  }

  function findDeepest(node: ASTNode): ASTNode | null {
    if (!nodeContains(node)) return null;
    for (const child of node.children) {
      const found = findDeepest(child);
      if (found) return found;
    }
    return node;
  }

  return findDeepest(root);
}

/**
 * Returns the symbol at the given position, if any.
 * @param root - Root AST node.
 * @param position - Position to look up.
 * @returns ASTSymbol or null if no named symbol is at the position.
 */
export function getSymbolAtPosition(root: ASTNode, position: Position): ASTSymbol | null {
  const node = getNodeAtPosition(root, position);
  if (!node || !node.name) return null;

  const SYMBOL_NODE_TYPES = new Set([
    'function_declaration',
    'type_declaration',
    'variable_declaration',
  ]);

  const KIND_MAP: Record<string, SymbolKind> = {
    function_declaration: 'function',
    type_declaration: 'class',
    variable_declaration: 'variable',
  };

  if (!SYMBOL_NODE_TYPES.has(node.type)) return null;

  return {
    name: node.name,
    kind: KIND_MAP[node.type] ?? 'variable',
    range: node.range,
    selectionRange: {
      start: node.range.start,
      end: { line: node.range.start.line, column: node.range.start.column + node.name.length },
    },
    uri: '',
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 GRAPHS — buildCallGraph, buildImportGraph
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Builds a call graph from an AST — mapping function declarations to the functions they call.
 * @param root - Root AST node.
 * @returns CallGraph with calls and calledBy maps.
 */
export function buildCallGraph(root: ASTNode): CallGraph {
  const calls = new Map<string, string[]>();
  const calledBy = new Map<string, string[]>();

  let currentFn: string | null = null;

  traverseAST(root, {
    onEnter(node) {
      if (node.type === 'function_declaration' && node.name) {
        currentFn = node.name;
        if (!calls.has(currentFn)) calls.set(currentFn, []);
      }
      // Heuristic: expression nodes inside a function that look like calls
      if (currentFn && node.type === 'expression' && node.name) {
        const callPattern = /^(\w+)\s*\(/.exec(node.name ?? '');
        if (callPattern?.[1]) {
          const callee = callPattern[1];
          const callerList = calls.get(currentFn) ?? [];
          if (!callerList.includes(callee)) callerList.push(callee);
          calls.set(currentFn, callerList);

          const calleeList = calledBy.get(callee) ?? [];
          if (!calleeList.includes(currentFn)) calleeList.push(currentFn);
          calledBy.set(callee, calleeList);
        }
      }
    },
    onExit(node) {
      if (node.type === 'function_declaration' && node.name === currentFn) {
        currentFn = null;
      }
    },
  });

  return { calls, calledBy };
}

/**
 * Builds an import dependency map from an AST.
 * @param root - Root AST node.
 * @returns ImportGraph mapping import sources to imported names.
 */
export function buildImportGraph(root: ASTNode): ImportGraph {
  const imports = new Map<string, string[]>();
  const exportedFrom = new Map<string, string>();

  traverseAST(root, {
    onEnter(node) {
      if (node.type !== 'import' || !node.name) return;
      // Name field contains the raw import text: 'import { A, B } from "source"'
      const fromMatch = /from\s+['"]([^'"]+)['"]/u.exec(node.name);
      const source = fromMatch?.[1] ?? node.name;

      const namesMatch = /\{([^}]+)\}/u.exec(node.name);
      const names: string[] = namesMatch
        ? namesMatch[1]!.split(',').map((n) => n.trim()).filter(Boolean)
        : ['*'];

      imports.set(source, [...(imports.get(source) ?? []), ...names]);
      for (const name of names) {
        exportedFrom.set(name, source);
      }
    },
  });

  return { imports, exportedFrom };
}
